import type { AutopilotTaskDto, TaskStepDto } from '@urbanx/agx.activities.client';
import type { TaskUpdatedNotification } from 'types/pushNotifications';

type NullableDueDateUpdate = {
  dueUtc?: string | null;
};

const doesTaskMatchUpdate = (
  task: Pick<AutopilotTaskDto, 'id'>,
  taskUpdate: TaskUpdatedNotification
) => task.id !== undefined && task.id === taskUpdate.task.id;

const mergeTaskSteps = (currentSteps?: TaskStepDto[], updatedSteps?: TaskStepDto[]) => {
  if (!updatedSteps) return currentSteps;
  const identifiedUpdatedSteps = updatedSteps.filter(
    (step) => step.order !== undefined || step.name !== undefined
  );

  if (identifiedUpdatedSteps.length === 0) return currentSteps;
  if (!currentSteps || currentSteps.length === 0) return identifiedUpdatedSteps;

  const doStepsMatch = (currentStep: TaskStepDto, updatedStep: TaskStepDto) => {
    if (currentStep.order !== undefined && updatedStep.order !== undefined) {
      return currentStep.order === updatedStep.order;
    }

    if (currentStep.name !== undefined && updatedStep.name !== undefined) {
      return currentStep.name === updatedStep.name;
    }

    return false;
  };

  const findMatchingUpdatedStep = (currentStep: TaskStepDto) =>
    identifiedUpdatedSteps.find((updatedStep) => doStepsMatch(currentStep, updatedStep));

  const mergedSteps = currentSteps.map((currentStep) => {
    const updatedStep = findMatchingUpdatedStep(currentStep);
    if (!updatedStep) return currentStep;

    return {
      ...currentStep,
      ...updatedStep,
      data: updatedStep.data ?? currentStep?.data,
      link: updatedStep.link ?? currentStep?.link,
    };
  });

  const appendedSteps = identifiedUpdatedSteps.filter(
    (updatedStep) => !currentSteps.some((currentStep) => doStepsMatch(currentStep, updatedStep))
  );

  return [...mergedSteps, ...appendedSteps];
};

export const applyTaskUpdateToTask = (
  task: AutopilotTaskDto,
  taskUpdate: TaskUpdatedNotification
): AutopilotTaskDto => {
  const updatedTask = taskUpdate.task;
  const updatedDueUtc = (updatedTask as NullableDueDateUpdate).dueUtc;

  return {
    ...task,
    ...updatedTask,
    contractId: updatedTask.contractId ?? undefined,
    assignedTo: updatedTask.assignedTo ?? undefined,
    dueUtc: updatedDueUtc === null ? undefined : (updatedDueUtc ?? task.dueUtc),
    taskSteps: mergeTaskSteps(task.taskSteps, updatedTask.taskSteps),
  };
};

export const applyTaskUpdateToTasks = (
  previousTasks: AutopilotTaskDto[],
  taskUpdate: TaskUpdatedNotification
) => {
  const taskIndex = previousTasks.findIndex((task) => doesTaskMatchUpdate(task, taskUpdate));

  if (taskIndex === -1) {
    return previousTasks;
  }

  const updatedTask = applyTaskUpdateToTask(previousTasks[taskIndex], taskUpdate);

  return [...previousTasks.slice(0, taskIndex), updatedTask, ...previousTasks.slice(taskIndex + 1)];
};
